/**
 * Maps localApiAdapter HTTP paths to DemoStorageService (in-memory + localStorage).
 * Used when REACT_APP_DEMO_MODE=true in the browser so the hosted demo never calls
 * /functions, localhost:5001, or Workers that do not allow the demo origin.
 */

import demoStorageService from './demoStorageService';
import { resolveCrudPath, CONFIG_PATHS, pathToEntity } from '../config/demoEntityRegistry';

function splitPathAndQuery(endpoint) {
  if (endpoint == null || typeof endpoint !== 'string') return { path: '/', query: '' };
  const q = endpoint.indexOf('?');
  if (q === -1) return { path: endpoint, query: '' };
  return { path: endpoint.slice(0, q), query: endpoint.slice(q + 1) };
}

async function crudEntity(entity, method, id, data) {
  await demoStorageService.initialize();
  switch (method) {
    case 'GET':
      if (id) return await demoStorageService.getById(entity, id);
      return await demoStorageService.getAll(entity);
    case 'POST':
      return await demoStorageService.create(entity, data || {});
    case 'PUT':
      return await demoStorageService.update(entity, id, data || {});
    case 'DELETE':
      if (!id && data && Array.isArray(data.ids)) {
        return await demoStorageService.bulkDelete(entity, data.ids);
      }
      if (!id && data && data.data && Array.isArray(data.data.ids)) {
        return await demoStorageService.bulkDelete(entity, data.data.ids);
      }
      return await demoStorageService.delete(entity, id);
    case 'PATCH':
      return await demoStorageService.update(entity, id, data || {});
    default:
      throw new Error(`Unsupported method: ${method}`);
  }
}

async function emptyArray() {
  return [];
}

async function dashboardStats(entity) {
  await demoStorageService.initialize();
  const count = await demoStorageService.count(entity);
  return { total: count, demo: true };
}

/**
 * @param {string} endpoint path often with query (e.g. /products?x=1)
 * @param {string} method
 * @param {object|null} data
 */
export async function demoHttpApiCall(endpoint, method = 'GET', data = null) {
  const { path: rawPath } = splitPathAndQuery(endpoint);
  let path = rawPath.replace(/\/+$/, '') || '/';
  if (path.startsWith('/api/')) path = path.slice(4) || '/';

  // Settings blob
  if (path === '/settings') {
    await demoStorageService.initialize();
    if (method === 'GET') return await demoStorageService.getSettings();
    if (method === 'PUT' || method === 'POST') {
      const next = { ...(await demoStorageService.getSettings()), ...(data || {}) };
      return await demoStorageService.updateSettings(next);
    }
  }

  if (path === '/health' && method === 'GET') {
    return { ok: true, demo: true };
  }

  // Config object endpoints (crm/config, etc.)
  if (CONFIG_PATHS.has(path)) {
    const entity = pathToEntity(path);
    await demoStorageService.initialize();
    if (method === 'GET') {
      const cfg = await demoStorageService.getConfig(entity);
      return cfg || {};
    }
    if (method === 'PUT' || method === 'POST') {
      return await demoStorageService.updateConfig(entity, data || {});
    }
  }

  // Registry-driven CRUD
  const resolved = resolveCrudPath(path);
  if (resolved) {
    if (resolved.isConfig) {
      await demoStorageService.initialize();
      if (method === 'GET') return (await demoStorageService.getConfig(resolved.entity)) || {};
      if (method === 'PUT' || method === 'POST') {
        return await demoStorageService.updateConfig(resolved.entity, data || {});
      }
    }
    return crudEntity(resolved.entity, method, resolved.id, data);
  }

  // Dashboard / stats stubs — return counts from seeded data when possible
  if (path.includes('/dashboard') || path.endsWith('/stats') || path === '/crm/stats' || path === '/crm/analyses-rapports') {
    if (method === 'GET') {
      if (path === '/crm/stats' || path === '/crm/analyses-rapports') {
        return { leads: 10, opportunities: 10, contacts: 10, campaigns: 10, demo: true };
      }
      const baseMatch = path.replace(/\/dashboard.*$/, '').replace(/\/stats$/, '');
      const entity = pathToEntity(baseMatch || path);
      try {
        return await dashboardStats(entity);
      } catch (_) {
        return { total: 10, count: 10, demo: true };
      }
    }
    if (method === 'POST' || method === 'PUT') return { success: true, demo: true };
  }

  // Read-only report/list stubs
  if (
    (path.includes('/analyses') || path.includes('/rapports') || path.includes('/agent')) &&
    method === 'GET'
  ) {
    return { items: [], total: 10, demo: true };
  }

  if (path === '/facturation/dashboard' && method === 'GET') {
    return { stats: { clients: 10, suppliers: 10, products: 10 }, demo: true };
  }

  if (path === '/comptabilite/dashboard' && method === 'GET') {
    await demoStorageService.initialize();
    const entries = await demoStorageService.count('comptabilite_entries');
    const accounts = await demoStorageService.count('comptabilite_accounts');
    const journal = await demoStorageService.count('comptabilite_journal');
    return {
      data: {
        total_entries: entries,
        total_accounts: accounts,
        total_journal: journal,
        validated: Math.floor(journal * 0.6),
        draft: Math.floor(journal * 0.4),
      },
      demo: true,
    };
  }

  if (path === '/comptabilite/dashboard/stats' && method === 'GET') {
    await demoStorageService.initialize();
    const journal = await demoStorageService.count('comptabilite_journal');
    const balance = await demoStorageService.count('comptabilite_balance');
    return {
      data: {
        entries: journal,
        balances: balance,
        accounts: await demoStorageService.count('comptabilite_accounts'),
      },
      demo: true,
    };
  }

  const comptabiliteRecentMatch = path.match(/^\/comptabilite\/(bilan|resultat|flux)\/recent$/);
  if (comptabiliteRecentMatch && method === 'GET') {
    const entityMap = {
      bilan: 'comptabilite_bilan',
      resultat: 'comptabilite_resultat',
      flux: 'comptabilite_flux',
    };
    const entity = entityMap[comptabiliteRecentMatch[1]];
    await demoStorageService.initialize();
    const rows = await demoStorageService.getAll(entity);
    return { data: rows.slice(0, 5), demo: true };
  }

  if (path === '/comptabilite/dashboard/activity/recent' && method === 'GET') {
    await demoStorageService.initialize();
    const rows = await demoStorageService.getAll('comptabilite_journal');
    return {
      data: rows.slice(0, 5).map((r) => ({
        id: r.id,
        label: r.libelle_ecriture || r.journal_ref,
        date: r.date_ecriture,
        amount: r.montant,
      })),
      demo: true,
    };
  }

  if (path.startsWith('/finances/stats')) {
    if (method === 'GET') return { total: 0, revenue: 0 };
    if (method === 'POST' || method === 'PUT') return { success: true, demo: true };
  }

  // Time tracking & misc lists — safe empty or stub (banking/treasury use registry CRUD)
  if (
    path.startsWith('/timetracking') ||
    path.startsWith('/management/departments') ||
    path.startsWith('/analytics') ||
    path.startsWith('/dash/')
  ) {
    if (method === 'GET') return emptyArray();
    if (method === 'POST' || method === 'PUT') return { success: true, demo: true };
    if (method === 'DELETE') return { success: true };
  }

  if (path.includes('documents-fields') || path.includes('documents-details')) {
    if (method === 'POST') return { success: true, skipped: true, demo: true };
  }

  console.warn('[demoHttpApiBridge] Unhandled demo path — returning safe default:', method, path);
  if (method === 'GET') return [];
  if (method === 'DELETE') return { success: true };
  return { success: true, demo: true, path };
}

export default { demoHttpApiCall };
